import { UserCircleIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import StatCell from '../../../components/analysis/StatCell';
import { getNormalizedSegments, getSegmentsArray } from '../../../utils/comparativeSegmentsHelper';

export default function ComparativeTestPerformanceTable({
  selectedSubject,
  comparativeScore,
  comparativeAccuracy,
  userAccuracy,
  userScore,
  totalMarks,
  comparativeSegmentsMeta,
}) {
  const [subjectComparativeScore, setSubjectComparativeScore] = useState(null);
  const [subjectComparativeAccuracy, setSubjectComparativeAccuracy] = useState(null);
  const [subjectUserAccuracy, setSubjectUserAccuracy] = useState(null);
  const [subjectUserScore, setSubjectUserScore] = useState(null);
  const [segments, setSegments] = useState({});

  useEffect(() => {
    const normalizedSegments = getNormalizedSegments(comparativeSegmentsMeta);
    setSegments(normalizedSegments);
  }, [comparativeSegmentsMeta]);

  useEffect(() => {
    if (!selectedSubject) return;

    const subjectId = selectedSubject?.id?.toString();

    setSubjectComparativeScore(comparativeScore?.filter((item) => item?.subject_id?.toString() === subjectId));
    setSubjectComparativeAccuracy(comparativeAccuracy?.filter((item) => item?.subject_id?.toString() === subjectId));
    setSubjectUserAccuracy(userAccuracy?.filter((item) => item?.subject_id?.toString() === subjectId));
    setSubjectUserScore(userScore?.filter((item) => item?.subject_id?.toString() === subjectId));
  }, [selectedSubject]);

  if (
    !selectedSubject ||
    !subjectComparativeScore ||
    !subjectComparativeAccuracy ||
    !subjectUserAccuracy ||
    !subjectUserScore
  )
    return null;

  if (!segments || Object.keys(segments).length === 0) return null;

  const subjectId = selectedSubject?.id?.toString();
  const total = totalMarks?.[subjectId];

  const renderStatCell = (color, top, bottom, showPercentage = false) =>
    top !== null && top !== undefined ? (
      <StatCell color={color} top={top} bottom={bottom} showPercentage={showPercentage} />
    ) : (
      <div className="flex items-center justify-center h-8 text-xs text-[var(--q3-neutral-default)]">No data</div>
    );

  const renderComparativeRow = (segment) => (
    <tr key={segment.key} className="border-b border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dim)]">
      <td className="h-16 w-75 border-r border-[var(--q3-stroke-light)]">
        <div className="flex items-center px-4 gap-4">
          <segment.icon size={24} className="text-[var(--q3-neutral-default)]" />
          <p className="truncate text-base font-semibold text-[var(--q3-neutral-default)]">{segment.title}</p>
        </div>
      </td>
      <td className="border-r border-[var(--q3-stroke-light)]">
        {renderStatCell('var(--q3-accent-normal)', subjectComparativeScore?.[0]?.values?.[segment.key], total)}
      </td>
      <td>
        {renderStatCell('var(--q3-green-normal)', subjectComparativeAccuracy?.[0]?.values?.[segment.key], 100, true)}
      </td>
    </tr>
  );

  const comparativeSegmentsArray = getSegmentsArray(segments, 'topper');

  return (
    <div className="overflow-x-auto rounded-b-xl border border-t-0 border-[var(--q3-stroke-light)]">
      <table className="text-sm w-full">
        <thead className="bg-[var(--q3-surface-dim)]">
          <tr className="h-16">
            <td className="py-3 px-4 w-[318px] text-start border-r border-[var(--q3-stroke-light)]">
              <div className="line-clamp-2 max-w-[300px] leading-snug">Students</div>
            </td>
            {['Score', 'Accuracy'].map((label, i, arr) => (
              <td
                key={i}
                className={`py-3 px-4 min-w-[285px] text-center ${i < arr.length - 1 && 'border-r border-[var(--q3-stroke-light)]'}`}
              >
                <div className="line-clamp-2 max-w-[285px] mx-auto leading-snug">{label}</div>
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* User row */}
          <tr className="border-b border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dim)]">
            <td className="h-16 w-75 border-r border-[var(--q3-stroke-light)]">
              <div className="flex items-center px-4 gap-4">
                <UserCircleIcon size={24} className="text-[var(--q3-neutral-default)]" />
                <p className="truncate text-base font-semibold text-[var(--q3-neutral-default)]">You</p>
              </div>
            </td>
            <td className="border-r border-[var(--q3-stroke-light)]">
              {renderStatCell('var(--q3-accent-normal)', subjectUserScore?.[0]?.score, total)}
            </td>
            <td>{renderStatCell('var(--q3-green-normal)', subjectUserAccuracy?.[0]?.accuracy, 100, true)}</td>
          </tr>

          {comparativeSegmentsArray.map((segment) => renderComparativeRow(segment))}
        </tbody>
      </table>
    </div>
  );
}
